import React from "react";
import { makeStyles, Theme, useTheme } from "@material-ui/core";
import { colorsLight } from "../../themes";

export type SortDirection = "asc" | "desc";

// sorting algorithm
export function createComparator<T>(
  accessor: (item: T) => any,
  direction: SortDirection = "asc"
): (a: T, b: T) => number {
  return (a, b) => {
    const aVal = accessor(a);
    const bVal = accessor(b);

    const aIsNullish = aVal == null;
    const bIsNullish = bVal == null;
    if (aIsNullish && bIsNullish) {
      return 0;
    }
    if (aIsNullish) {
      return direction === "asc" ? 1 : -1;
    }
    if (bIsNullish) {
      return direction === "asc" ? -1 : 1;
    }

    const aNorm = typeof aVal === "string" ? aVal.toLowerCase() : aVal;
    const bNorm = typeof bVal === "string" ? bVal.toLowerCase() : bVal;

    if (typeof aNorm === "number" && typeof bNorm === "number") {
      return direction === "asc" ? aNorm - bNorm : bNorm - aNorm;
    }

    return direction === "asc"
      ? String(aNorm).localeCompare(String(bNorm))
      : String(bNorm).localeCompare(String(aNorm));
  };
}

export function isMultiRowPanel<T>(panel: T[] | T): panel is T[] {
  return Array.isArray(panel);
}
interface StyleProps {
  isTitle?: boolean;
}
// styles
export const useTableStyles = makeStyles<Theme>((theme) => ({
  table: {
    borderCollapse: "separate",
    "& .MuiTableBody-root:nth-last-child(n+2):after": {
      // prettier-ignore
      content: "''",
      display: "block",
      height: theme.spacing(1),
    },
  },
  sortHeader: {
    cursor: "pointer",
    "&:hover": {
      color: colorsLight.primary.light,
    },
    "&.MuiTableSortLabel-active": {
      "&:hover": {
        color: theme.palette.type === "dark" ? theme.palette.common.white : "inherit",
      },
    },
  },
  noSortHeader: {
    cursor: "default",
    // Remove hover effects entirely
    "&:hover": {
      color: "inherit",
    },
    "&.MuiTableSortLabel-active": {
      "&:hover": {
        color: "inherit",
      },
    },
  },
  header: {
    padding: theme.spacing(1, 1.5),
    whiteSpace: "nowrap",
    "&:first-child": {
      paddingLeft: theme.spacing(1),
    },
    "&.MuiTableCell-head": {
      lineHeight: "1rem",
    },
    border: "none",
  },
  noSpacingHeader: {
    padding: theme.spacing(1, 1.5),
    whiteSpace: "nowrap",
    "&:first-child": {
      paddingLeft: theme.spacing(0),
    },
    "&.MuiTableCell-head": {
      lineHeight: "1rem",
    },
    border: "none",
  },
  tableCell: {
    backgroundColor: theme.palette.table.panelBackground,
    padding: theme.spacing(1.5),
    verticalAlign: "top",
  },
  cellStyles: (props: StyleProps) => ({
    fontSize: props.isTitle ? 14 : 13,
    lineHeight: props.isTitle ? "16.8px" : "normal",
    fontWeight: props.isTitle ? 600 : 400,
  }),
}));

// bitmasks for cell position within a panel
export const TOP = 1 << 3;
export const BOTTOM = 1 << 2;
export const LEFT = 1 << 1;
export const RIGHT = 1 << 0;

export function usePanelTableCellStyles(position: number, isTitle?: boolean) {
  const theme = useTheme();

  return {
    borderTop: position & TOP ? `1px solid ${theme.palette.divider}` : "none",
    borderBottom: position & BOTTOM ? `1px solid ${theme.palette.divider}` : "none",
    borderLeft: position & LEFT ? `1px solid ${theme.palette.divider}` : "none",
    borderRight: position & RIGHT || isTitle ? `1px solid ${theme.palette.divider}` : "none",
    paddingTop: position & TOP ? theme.spacing(1) : undefined,
    paddingBottom: position & BOTTOM ? theme.spacing(1) : undefined,
    paddingLeft: position & LEFT ? theme.spacing(1) : undefined,
    paddingRight: position & RIGHT ? theme.spacing(1) : undefined,
    borderTopLeftRadius: position & TOP && position & LEFT ? 4 : undefined,
    borderTopRightRadius: position & TOP && (position & RIGHT || isTitle) ? 4 : undefined,
    borderBottomLeftRadius: position & BOTTOM && position & LEFT ? 4 : undefined,
    borderBottomRightRadius: position & BOTTOM && position & RIGHT ? 4 : undefined,
    backgroundColor: theme.palette.table.panelBackground,
    padding: theme.spacing(1),
    verticalAlign: "top",
  };
}

// component types
export interface PanelRowProps<T> {
  rowData: T | null;
  columns: Column<T>[];
  row_idx: number;
  numRows: number;
  isTitle?: boolean;
  titleText?: string;
  authStatusDisplayConfig?: { [key: string]: string };
}

export type TableRowData<T> = T | { isTitle: true; title: string };

export interface Column<T> {
  columnName: string;
  value: (arg: T) => React.ReactNode;
  fillPanel?: boolean /* If true then the first cell of this column will fill the entire panel */;
  width?: string;
  customWidth?: (panels: TableRowData<T>[][]) => string;
}

export interface TableWithPanelsProps<T> {
  /* displays panels with multiple rows in each if
    supplied a 2D array. displays one row per panel if given
    a flat array. */
  panels: TableRowData<T>[] | TableRowData<T>[][];
  columns: Column<T>[];
  noSpacing?: boolean;
  allowSort?: boolean;
  authStatusDisplayConfig?: { [key: string]: string };
}

export interface PanelCellProps<T> {
  column: Column<T>;
  position: number;
  rowData: T | null;
  numRows: number;
  isTitle?: boolean;
  titleText?: string;
  colSpan?: number;
  authStatusDisplayConfig?: { [key: string]: string };
}
